/**
 * Fleet-wide app availability — `GET /v1/apps/licences/summary` (#2621).
 *
 * The sibling read `appLicences.api.ts` answers "which tenants can see this one app". This one
 * answers "how many apps can no tenant see", once, for the whole fleet. The console needs the
 * second question because the first one costs one request per app and stops at
 * `AVAILABILITY_FANOUT_CAP`, so a fan-out count cannot state a fleet-wide fact.
 *
 * The gate is a site-wide `cp:app:read` ({@link AVAILABILITY_SUMMARY_PERMISSION}), the key of the
 * per-app licence read. A site-wide holder reads every app's licences, so the count discloses
 * nothing more. A per-app `cp:app:read` grant opens one app's list and not this count. A refusal is
 * a fact about the reader. Rendering it as `0` would tell that reader the fleet is fine.
 * `AvailabilitySummaryState` in `panes/availability-model.ts` is where that distinction is kept.
 *
 * This module is a second copy of the producer's path and of its response shape, and R-020 settles
 * both. `@zion/cp-admin-ui` has no dependency on `@zion/release-engine-v3` and neither shape has a
 * `@zion/sdk-contracts` entry, so the console declares its own on `appLicencesPath`'s and
 * `OrgAppLicenceView`'s precedents. What holds: `W3-11` in release-engine reds when a field is
 * added on the producer. What does not hold: a rename on either side, because `api.get<T>` is a
 * cast and this package's fixtures are literals.
 *
 * The read gate is declared here, and only here: {@link AVAILABILITY_SUMMARY_PERMISSION}. D5 of
 * 2026-10-20-cp-permissioning moved the per-app read to `cp:app:read` and left this route on
 * `cp:billing:read`. On 2026-10-27 this route moved to `cp:app:read` too, because A-008 of
 * 2026-10-26-appdev-permissions gave every `app_developer` the read and no billing key. The
 * constant stays its own, so the gate of each route has one name.
 */
import { api } from '../platform/http/client';

/**
 * The whole path literal, in one constant.
 *
 * `dev/__tests__/fixture-routes-answer.test.ts` derives the dev-harness obligation by grepping
 * `/v1/…` string literals out of `src/api/**`, so a path assembled from a prefix escapes the gate
 * and the screen renders blank in the owner's review harness with nothing to say why. That is
 * `appLicencesPath`'s stated rule, applied to a path with no parameter.
 *
 * Mirrors `APP_AVAILABILITY_SUMMARY_PATH` in release-engine's
 * `modules/app-licences/availability-summary.contract.ts`. The handler that discharges the
 * obligation is in `src/dev/appAdminFixtures.ts` (R-019).
 */
export const APP_AVAILABILITY_SUMMARY_PATH = '/v1/apps/licences/summary';

/**
 * The gate of {@link APP_AVAILABILITY_SUMMARY_PATH}: `cp:app:read`, site-wide. The producer's
 * `@RequirePermission('cp:app:read')` on the summary route decides. `CpAdminApp` pre-checks it
 * before the one read, and `AppCatalogPane` names it when the count is refused.
 */
export const AVAILABILITY_SUMMARY_PERMISSION = 'cp:app:read';

/**
 * Two fleet-wide counts.
 *
 * Hand-declared, mirroring `AppAvailabilitySummaryView` in release-engine's
 * `availability-summary.contract.ts`, on `OrgAppLicenceView`'s precedent: neither
 * `@zion/sdk-contracts` nor `@zion/sdk-cp-dp-contracts` has an entry for either shape.
 *
 * `notOffered` counts apps with no `release.org_app_licences` row whose `revoked_at` is null.
 * `appsTotal` counts every `release.apps` row, the catalogue's own denominator. Both are
 * non-negative integers and `notOffered <= appsTotal`, which the producer's
 * `appAvailabilitySummarySchema` refines on the way out.
 *
 * `appsTotal` is read by no console module today. It exists for that refinement and for a later
 * caption, and R-020 keeps it for that.
 */
export interface AppAvailabilitySummary {
  readonly appsTotal: number;
  readonly notOffered: number;
}

/**
 * One fleet-wide read.
 *
 * Resolves with the two counts for a principal holding {@link AVAILABILITY_SUMMARY_PERMISSION}. Rejects with
 * `ApiError(403)` without it, and that rejection is a fact about the reader, never a zero: the one
 * place it becomes a state is `toSummaryState` in `panes/availability-model.ts`. Rejects with
 * `ApiError(<status>)` for any other refusal and with the underlying error for a network fault.
 * It takes no argument and reads nothing per app, so it is issued once per console load.
 *
 * ⛔ `api.get<T>` is a CAST, not a parse. The producer's `appAvailabilitySummarySchema` refines the
 * row on the way out, and nothing on this side re-checks it — which is the gap `W3-11` covers on
 * the producer and R-020 states here.
 */
export function fetchAvailabilitySummary(): Promise<AppAvailabilitySummary> {
  return api.get<AppAvailabilitySummary>(APP_AVAILABILITY_SUMMARY_PATH);
}
